//TODO: rename class
import { inject, Injectable } from '@angular/core';
import { BaseTreeService } from './base-tree.service';
import { isKpfFileNode, isKpfFolderNode, KpfFileNode, KpfFolderNode, KpfNode, TreeNodeType } from '../models/tree-node';
import { FileInfo, FileTreeNode } from '../../shared/interfaces';
import { TreeSortMode } from '../../shared/models';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { ProjectService } from '../../shared/services/project.service';
import { FileTransferService, TransferStatus } from '../../shared/services/signed-url';
import { KpfBuildFolderParams, KpfTreeBuildParams, KpfTreePopulateParams } from '../models';
import { ProjectFileService } from '../../shared/services/project-file.service';

@Injectable({
  providedIn: 'root',
})
export class KpfTreeService extends BaseTreeService<KpfNode> {
  private readonly projectService = inject(ProjectService);
  private readonly projectFileService = inject(ProjectFileService);
  private readonly fileTransferService = inject(FileTransferService);
  private projectId: string = '';
  private previousLoadedFolder: KpfFolderNode | null = null;

  buildTree(params: KpfTreeBuildParams) {
    const { node, sortMode, projectId } = params;
    const rootNode: KpfFolderNode = {
      id: node.id,
      name: node.name,
      type: TreeNodeType.KpfRoot,
      size: node.size,
      lastUpdated: node.lastUpdated,
      isExpandable: true,
      isSelected: true,
      isExpanded: true,
      subFolderIds: [],
      children: [] as KpfNode[],
      parent: null,
      depth: 0,
      fileIds: [],
      filesStatus: 'pending',
    };
    this.projectId = projectId;
    this.state = {
      root: rootNode,
      paths: {
        [node.id]: [node.id],
      },
      selectedNode: null,
      expandedNodes: [],
    };

    this.populateKPFData(node, rootNode, sortMode);
    this.updateState({
      root: rootNode,
    });
  }

  sortTree(sortMode: TreeSortMode, updateState?: boolean) {
    if (!this.state.root) return;
    this.sortNode(this.state.root, sortMode);
    if (updateState) {
      this.updateState({
        root: this.state.root,
      });
    }
  }

  private populateKPFData(node: FileTreeNode, parentNode: KpfFolderNode, sortMode: TreeSortMode) {
    // Skip path creation for root node since it's already set in buildTree
    if (parentNode.id !== this.state.root?.id && parentNode.id in this.state.paths) {
      this.state.paths[node.id] = this.state.paths[parentNode.id].concat(node.id);
    }

    if (node.children) {
      Object.entries(node.children).forEach(([key, value]) => {
        if (key === 'files') {
          if (Array.isArray(value) && value.length) {
            parentNode.filesStatus = 'pending';
          }
          return;
        }

        const childFolder = value as FileTreeNode;
        if (childFolder.type === 'DIR') {
          const folderNode: KpfFolderNode = {
            id: childFolder.id,
            name: childFolder.name,
            type: TreeNodeType.KpfFolder,
            size: childFolder.size,
            lastUpdated: childFolder.lastUpdated,
            isExpandable: true,
            subFolderIds: [],
            fileIds: [],
            children: [] as KpfNode[],
            parent: parentNode,
            depth: parentNode.depth + 1,
            filesStatus: 'pending',
          };

          parentNode.children.push(folderNode);
          this.populateKPFData(childFolder, folderNode, sortMode);
        }
      });

      this.sortChildren(parentNode, sortMode);

      if (parentNode.filesStatus !== 'pending') {
        parentNode.filesStatus = 'loaded';
      }
    }
  }

  private sortNode(node: KpfNode, sortMode: TreeSortMode) {
    if (!node) return;

    this.sortChildren(node, sortMode);

    node.children.forEach((child) => {
      if (isKpfFolderNode(child)) {
        this.sortNode(child, sortMode);
      }
    });
  }

  private sortChildren(node: KpfNode, sortMode: TreeSortMode) {
    node.children.sort((a, b) => {
      if (a.type === TreeNodeType.KpfFile && b.type === TreeNodeType.KpfFile) {
        const fileA = a as KpfFileNode;
        const fileB = b as KpfFileNode;
        return this.sortFiles(fileA, fileB, sortMode);
      } else if (a.type === TreeNodeType.KpfFolder && b.type === TreeNodeType.KpfFolder) {
        const folderA = a as KpfFolderNode;
        const folderB = b as KpfFolderNode;
        return this.sortFolders(folderA, folderB, sortMode);
      }

      //folders are higher priority than files
      return a.type === TreeNodeType.KpfFolder ? -1 : 1;
    });
  }

  private sortFiles(a: KpfFileNode, b: KpfFileNode, sortMode: TreeSortMode): number {
    switch (sortMode) {
      case TreeSortMode.lastUpdated:
      case TreeSortMode.lastUpload:
        return (b.data.timeCreated || 0) - (a.data.timeCreated || 0);
      case TreeSortMode.firstUpload:
        return (a.data.timeCreated || 0) - (b.data.timeCreated || 0);
      default:
        return (a.data.displayName || '').localeCompare(b.data.displayName || '', undefined, { sensitivity: 'base' });
    }
  }

  private sortFolders(a: KpfFolderNode, b: KpfFolderNode, sortMode: TreeSortMode): number {
    switch (sortMode) {
      case TreeSortMode.lastUpdated:
      case TreeSortMode.lastUpload:
        return (b.lastUpdated || 0) - (a.lastUpdated || 0);
      case TreeSortMode.firstUpload:
        return (a.lastUpdated || 0) - (b.lastUpdated || 0);
      default:
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    }
  }

  private cleanPreviousLoadedFolder() {
    if (!this.previousLoadedFolder) {
      return;
    }

    const files = this.previousLoadedFolder.children.filter((c) => isKpfFileNode(c));
    files.forEach((file) => {
      file.isSelected = false;
      file.isExpanded = false;
      delete this.state.paths[file.id];
    });
    this.previousLoadedFolder.children = this.previousLoadedFolder.children.filter((c) => !isKpfFileNode(c));

    this.previousLoadedFolder.fileIds = [];
    this.previousLoadedFolder.filesStatus = 'pending';
    this.previousLoadedFolder = null;
  }

  buildFolderNode(params: KpfBuildFolderParams): KpfFolderNode | null {
    const { treeNode, parentId, sortMode, updateState } = params;

    const parentNode = this.findNodeById(parentId);
    if (!parentNode) {
      return null;
    }

    const folderNode: KpfFolderNode = {
      id: treeNode.id,
      name: treeNode.name,
      type: TreeNodeType.KpfFolder,
      size: treeNode.size,
      lastUpdated: treeNode.lastUpdated,
      isExpandable: true,
      subFolderIds: [],
      fileIds: [],
      children: [] as KpfNode[],
      parent: parentNode,
      depth: parentNode.depth + 1,
      filesStatus: 'pending',
    };

    if (parentId in this.state.paths) {
      this.state.paths[treeNode.id] = this.state.paths[parentId].concat(treeNode.id);
    }
    parentNode.children.push(folderNode);
    this.sortChildren(parentNode, sortMode);

    if (updateState) {
      this.updateState({
        root: this.state.root,
      });
    }

    return folderNode;
  }

  buildFileNode(file: FileInfo, parentNode: KpfFolderNode, updateStateAfterAdd: boolean = false): KpfFileNode {
    const fileNode: KpfFileNode = {
      id: file.id,
      name: file.displayName,
      type: TreeNodeType.KpfFile,
      isSelected: false,
      isExpanded: false,
      data: file,
      parent: parentNode,
      depth: parentNode.depth + 1,
      children: [],
      isExpandable: false,
    };
    parentNode.fileIds.push(file.id);
    parentNode.children.push(fileNode);
    if (parentNode.id in this.state.paths) {
      this.state.paths[file.id] = this.state.paths[parentNode.id].concat(file.id);
    }
    if (updateStateAfterAdd) {
      this.updateState({
        root: this.state.root,
        selectedNode: this.state.selectedNode,
      });
    }
    return fileNode;
  }

  populateFiles(params: KpfTreePopulateParams): Observable<KpfFolderNode | null> {
    const { folderId, abort$, sortMode } = params;
    const folderNode = this.findNodeById(folderId) as KpfFolderNode;
    if (!folderNode) {
      return of(null);
    }

    const subject = new Subject<KpfFolderNode>();
    folderNode.filesStatus = 'loading';
    this.projectFileService
      .getFilesInKeyProjectFolder(this.projectId, folderId)
      .pipe(takeUntil(abort$))
      .subscribe({
        next: (files: FileInfo[]) => {
          this.cleanPreviousLoadedFolder();

          files.forEach((file) => {
            this.buildFileNode(file, folderNode);
          });
          this.getFilesBeingUploaded(folderNode);
          this.sortChildren(folderNode, sortMode);
          folderNode.filesStatus = 'loaded';
          this.previousLoadedFolder = folderNode;

          subject.next(folderNode);
          subject.complete();
        },
        error: (error) => {
          folderNode.filesStatus = 'pending';
          subject.error(error);
          subject.complete();
        },
      });

    return subject.asObservable();
  }

  // Get files being uploaded to a node
  getFilesBeingUploaded(node: KpfFolderNode) {
    const activeStatus = [TransferStatus.SENDING, TransferStatus.WAITING];
    const files = this.fileTransferService.filesToUpload.filter(
      (f) => f.parentId === node.id && activeStatus.includes(f.uploadStatusCode)
    );

    files.forEach((file) => {
      if (!file?.dbFileObject?.id) {
        return;
      }

      file.dbFileObject.isUploading = true;
      this.buildFileNode(file.dbFileObject, node);
    });
  }
}
